// "Sign in with Apple JS" integration for the web PWA.
//
// The native app uses `expo-apple-authentication`, which does not work on the
// web. On the web we load Apple's JS SDK and use the popup flow. The popup MUST
// be opened from a direct user gesture (a click handler), otherwise the browser
// blocks it — so callers should invoke `signInWithApple()` straight from the
// button's onPress, not via an async/redux roundtrip.

import {
	APPLE_WEB_SERVICE_ID,
	APPLE_WEB_REDIRECT_URI
} from "../config/constants";

const APPLE_SDK_URL =
	"https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";

let sdkPromise = null;
let initialized = false;

// The redirect URI must (a) be on this PWA's own origin so the popup can relay
// the result via postMessage, and (b) be registered as a Return URL for the
// Services ID in Apple. Deriving it from the current origin makes it follow the
// environment (staging vs production) automatically. APPLE_WEB_REDIRECT_URI can
// force a fixed value if ever needed.
const getRedirectUri = () =>
	APPLE_WEB_REDIRECT_URI ||
	(typeof window !== "undefined" ? `${window.location.origin}/` : "");

// Load the AppleID JS SDK once (no-op if already present).
const loadAppleSdk = () => {
	if (typeof window !== "undefined" && window.AppleID) {
		return Promise.resolve();
	}
	if (sdkPromise) {
		return sdkPromise;
	}
	sdkPromise = new Promise((resolve, reject) => {
		const script = document.createElement("script");
		script.src = APPLE_SDK_URL;
		script.async = true;
		script.onload = () => resolve();
		script.onerror = () =>
			reject(new Error("Failed to load the Sign in with Apple SDK"));
		document.head.appendChild(script);
	});
	return sdkPromise;
};

const ensureInitialized = async () => {
	await loadAppleSdk();
	if (!initialized) {
		window.AppleID.auth.init({
			clientId: APPLE_WEB_SERVICE_ID,
			scope: "name email",
			redirectURI: getRedirectUri(),
			usePopup: true
		});
		initialized = true;
	}
};

// Opens the Apple popup and returns the data our server expects:
// { authorizationCode, redirectUri, fullName: { givenName, familyName } }.
// Apple only returns the user's name on the FIRST authorization, so fullName
// may be empty on subsequent sign-ins.
export const signInWithApple = async () => {
	await ensureInitialized();
	const response = await window.AppleID.auth.signIn();
	const name = response?.user?.name || {};
	return {
		authorizationCode: response?.authorization?.code,
		redirectUri: getRedirectUri(),
		fullName: {
			givenName: name.firstName || null,
			familyName: name.lastName || null
		}
	};
};

// The SDK rejects with an object like { error: "popup_closed_by_user" } when
// the user cancels. Lets callers show a "cancelled" message vs a real error.
export const isAppleCancel = error =>
	Boolean(
		error &&
			(error.error === "popup_closed_by_user" ||
				error.error === "user_cancelled_authorize" ||
				error.error === "user_trigger_new_signin_flow")
	);
